import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Minus, ShoppingCart, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImg, fmt } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [variantLabel, setVariantLabel] = useState(null);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      const variants = r.data?.variants || [];
      if (variants.length) {
        // Pick first in-stock variant; fall back to first.
        const firstInStock = variants.find((v) => Number(v.stock) > 0);
        setVariantLabel((firstInStock || variants[0]).label);
      }
    }).catch(() => setProduct(false));
  }, [slug]);

  const variants = product?.variants || [];
  const hasVariants = variants.length > 0;
  const selectedVariant = useMemo(
    () => (hasVariants ? variants.find((v) => v.label === variantLabel) : null),
    [variants, variantLabel, hasVariants],
  );

  if (product === false) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-400">Product not found.</div>;
  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20 text-slate-400">Loading…</div>;

  const displayPrice = hasVariants ? Number(selectedVariant?.price || 0) : Number(product.price);
  const displayStock = hasVariants ? Number(selectedVariant?.stock || 0) : Number(product.stock);
  const displayImage = selectedVariant?.image_url || product.image_url;

  const handleAdd = () => {
    if (hasVariants && !selectedVariant) {
      toast.error("Please choose a size");
      return;
    }
    add(product, qty, selectedVariant || null);
    const label = selectedVariant?.label ? ` (${selectedVariant.label})` : "";
    toast.success(`${product.name}${label} added to cart`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid={`product-detail-${product.slug}`}>
      <Link to="/shop" className="inline-flex items-center gap-1.5 text-xs label-mono text-slate-400 hover:text-cyan-400 mb-8" data-testid="back-to-shop">
        <ChevronLeft size={14} /> Back to catalog
      </Link>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="aspect-square bg-slate-900 border border-white/5 overflow-hidden relative">
          <img src={resolveImg(displayImage)} alt={product.name} className="w-full h-full object-cover transition-opacity duration-200" data-testid="product-detail-image" />
          <div className="absolute top-4 left-4 label-mono text-cyan-400 bg-slate-950/80 px-3 py-1.5 border border-cyan-500/20">
            {product.category}
          </div>
        </div>

        <div>
          <div className="label-mono text-slate-500 mb-3">SKU NGX-{product.id.slice(0, 6).toUpperCase()}</div>
          <h1 className="text-4xl md:text-5xl font-medium tracking-tight">{product.name}</h1>
          <p className="mt-4 text-slate-300 leading-relaxed">{product.short_description}</p>

          {hasVariants && (
            <div className="mt-8" data-testid="variant-selector">
              <div className="label-mono text-cyan-400 mb-3">Select Size</div>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => {
                  const out = Number(v.stock) <= 0;
                  const active = v.label === variantLabel;
                  return (
                    <button
                      key={v.label}
                      type="button"
                      disabled={out}
                      onClick={() => setVariantLabel(v.label)}
                      data-testid={`variant-${v.label.toLowerCase()}`}
                      className={`px-4 h-11 border text-sm font-medium transition ${
                        active
                          ? "border-cyan-500 bg-cyan-500/10 text-cyan-400"
                          : out
                            ? "border-white/5 text-slate-600 line-through cursor-not-allowed"
                            : "border-white/10 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-400"
                      }`}
                    >
                      <div className="flex flex-col items-start gap-0.5">
                        <span>{v.label}</span>
                        <span className="font-mono text-[10px] opacity-80">{fmt(v.price)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-baseline gap-4">
            <div className="font-mono text-4xl text-cyan-400" data-testid="product-price">{fmt(displayPrice)}</div>
            <div className="label-mono text-slate-500" data-testid="product-stock">
              {displayStock > 0 ? `${displayStock} in stock` : "Out of stock"}
            </div>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-white/10">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-12 hover:bg-white/5" data-testid="qty-dec"><Minus size={14} className="mx-auto" /></button>
              <span className="w-12 text-center font-mono" data-testid="qty-display">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-10 h-12 hover:bg-white/5" data-testid="qty-inc"><Plus size={14} className="mx-auto" /></button>
            </div>
            <button
              onClick={handleAdd}
              disabled={displayStock < 1}
              data-testid="add-to-cart-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-800 disabled:text-slate-500 text-slate-950 font-semibold h-12 px-6"
            >
              <ShoppingCart size={16} /> Add to Cart
            </button>
          </div>

          {/* Specs */}
          <div className="mt-10 border border-white/5">
            <div className="label-mono text-cyan-400 px-5 py-3 border-b border-white/5">Specifications</div>
            <dl className="divide-y divide-white/5">
              {[
                ["Purity", product.purity],
                ["Molecular Weight", product.molecular_weight || "—"],
                ["Sequence", product.sequence || "—"],
                ["CAS Number", product.cas_number || "—"],
                ["Storage", "Lyophilized −20°C"],
                ["Form", "White lyophilized powder"],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-2 px-5 py-3 text-sm">
                  <dt className="label-mono text-slate-500">{k}</dt>
                  <dd className="font-mono text-slate-200 break-all">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="mt-8">
            <div className="label-mono text-cyan-400 mb-3">Description</div>
            <p className="text-slate-300 leading-relaxed whitespace-pre-line">{product.description}</p>
          </div>

          <div className="mt-8 p-5 border border-amber-500/20 bg-amber-500/5">
            <div className="label-mono text-amber-400 mb-2">⚠ Research Use Only</div>
            <p className="text-xs text-amber-200/80 leading-relaxed">
              This product is intended solely for in-vitro laboratory research. Not for human or veterinary use, food, cosmetics, or any clinical application.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
